import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import SignatureCanvas from '../../components/SignatureCanvas';
import Spinner from '../../bootstrap/Spinner';
import {
  PageContainer,
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
  InfoRow,
  InfoLabel,
  InfoValue,
  Divider,
  SubmitButton,
  SuccessMessage,
  ErrorMessage,
  ExpiredMessage,
  LoadingContainer,
  DocumentTypeLabel,
  BookingNumber,
  TimerText,
} from './DocumentSignature.styles';

/**
 * DocumentSignature Page
 *
 * Public signature page - no login required (token-based authentication)
 * Story 1: Digital Document Generation & Signature System
 *
 * URL: /signature/:token
 */
const DocumentSignature = () => {
  const { token } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [hoursRemaining, setHoursRemaining] = useState(0);

  useEffect(() => {
    if (token) {
      fetchDocumentData();
    }
  }, [token]);

  const fetchDocumentData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/view/${token}`
      );

      if (response.data.status === 'success') {
        setDocumentData(response.data.data.document);
        setBookingData(response.data.data.booking);
        setHoursRemaining(response.data.data.hours_remaining);

        // Check if already signed
        if (response.data.data.document.status === 'signed') {
          setSigned(true);
        }
      } else {
        setError(response.data.message || 'Dokumen tidak ditemukan');
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      setError(
        err.response?.data?.message ||
          'Dokumen tidak ditemukan atau sudah kadaluarsa'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureChange = (signatureDataUrl) => {
    setSignatureData(signatureDataUrl);
  };

  const handleSubmit = async () => {
    if (!signatureData) {
      toast.error('Mohon tanda tangani dokumen terlebih dahulu');
      return;
    }

    if (!window.confirm('Apakah Anda yakin akan menandatangani dokumen ini?')) {
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/sign/${token}`,
        {
          signature_data: signatureData,
          customer_id: bookingData?.id || 0,
        }
      );

      if (response.data.status === 'success') {
        toast.success('Dokumen berhasil ditandatangani!');
        setSigned(true);

        // Redirect to success page or show success message
        setTimeout(() => {
          // You can redirect to a thank you page or home
          // history.push('/');
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Gagal menandatangani dokumen');
      }
    } catch (err) {
      console.error('Error signing document:', err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          'Gagal menandatangani dokumen'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      nota_pesanan: 'Nota Pesanan',
      bukti_pembayaran: 'Bukti Pembayaran',
      surat_perintah_jalan: 'Surat Perintah Jalan (SPJ)',
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spinner />
          <p>Memuat dokumen...</p>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Card>
          <CardBody>
            <ErrorMessage>
              <h2>❌ Dokumen Tidak Ditemukan</h2>
              <p>{error}</p>
              <p style={{ fontSize: '14px', marginTop: '16px' }}>
                Link mungkin sudah kadaluarsa atau tidak valid.
              </p>
            </ErrorMessage>
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  if (hoursRemaining <= 0) {
    return (
      <PageContainer>
        <Card>
          <CardBody>
            <ExpiredMessage>
              <h2>⏱️ Link Sudah Kadaluarsa</h2>
              <p>
                Link dokumen ini sudah tidak berlaku. Silakan hubungi customer
                service untuk mendapatkan link baru.
              </p>
            </ExpiredMessage>
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  if (signed) {
    return (
      <PageContainer>
        <Card>
          <CardBody>
            <SuccessMessage>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
              <h2>Dokumen Berhasil Ditandatangani</h2>
              <p>Terima kasih telah menandatangani dokumen.</p>
              <p style={{ fontSize: '14px', marginTop: '16px', color: '#666' }}>
                No. Booking: <strong>{bookingData?.booking_number}</strong>
              </p>
            </SuccessMessage>
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <CardHeader>
          <DocumentTypeLabel>
            {getDocumentTypeLabel(documentData?.document_type)}
          </DocumentTypeLabel>
          <CardTitle>Tanda Tangan Dokumen</CardTitle>
          <CardSubtitle>
            Silakan tanda tangani dokumen booking Anda di bawah
          </CardSubtitle>
          {hoursRemaining > 0 && (
            <TimerText>
              ⏰ Link aktif {hoursRemaining} jam lagi
            </TimerText>
          )}
        </CardHeader>

        <CardBody>
          {/* Booking Information */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
              Detail Booking
            </h3>

            <InfoRow>
              <InfoLabel>No. Booking:</InfoLabel>
              <InfoValue>
                <BookingNumber>{bookingData?.booking_number}</BookingNumber>
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Nama Pemesan:</InfoLabel>
              <InfoValue>{bookingData?.customer_name}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Telepon:</InfoLabel>
              <InfoValue>{bookingData?.customer_phone}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Rute:</InfoLabel>
              <InfoValue>{bookingData?.route}</InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Tanggal Keberangkatan:</InfoLabel>
              <InfoValue>
                {formatDate(bookingData?.departure_date)} - {bookingData?.departure_time}
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Total Harga:</InfoLabel>
              <InfoValue style={{ fontWeight: '700', color: '#27ae60' }}>
                {formatCurrency(bookingData?.contract_price)}
              </InfoValue>
            </InfoRow>
          </div>

          <Divider />

          {/* Signature Canvas */}
          <SignatureCanvas
            onSignatureChange={handleSignatureChange}
            height={200}
          />

          {/* Submit Button */}
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <SubmitButton
              onClick={handleSubmit}
              disabled={!signatureData || submitting}
            >
              {submitting ? '⏳ Mengirim...' : '✍️ Tanda Tangan & Kirim'}
            </SubmitButton>
          </div>

          <p style={{ textAlign: 'center', fontSize: '12px', color: '#7f8c8d', marginTop: '16px' }}>
            Dengan menandatangani, Anda menyetujui detail booking di atas.
          </p>
        </CardBody>
      </Card>
    </PageContainer>
  );
};

export default DocumentSignature;
